import React, { useState, useEffect, useCallback } from 'react';
import { Recipe } from '../../api/Types';
import { useAuth } from '../../context/AuthContext';

import useDismissibleOverlay from '../../hooks/ui/useDismissibleOverlay';
import useEditRecipeImage from '../../hooks/recipes/useEditRecipeImage';
import useRecipeEditors from '../../hooks/recipes/useRecipeEditors';
import useRecipeTagOptions from '../../hooks/recipes/useRecipeTagOptions';
import useRecipeFormFeedback from '../../hooks/forms/useRecipeFormFeedback';
import useEditRecipeFormActions from '../../domains/recipes/hooks/useEditRecipeFormActions';

import RecipeFormFields from './RecipeFormFields';
import RecipeFormAlert from './RecipeFormAlert';
import { Alert, Box, Button, Card, CardTitle, FileInput, Flex, Form, FormField, Image, PlainButton } from '../ui';

interface EditRecipeFormProps {
  recipe: Recipe;
  onRecipeUpdated: () => void;
  onRecipeDeleted: () => void;
  onCancel: () => void;
}

const EditRecipeForm: React.FC<EditRecipeFormProps> = ({ recipe, onRecipeUpdated, onRecipeDeleted, onCancel }) => {
  const [title, setTitle] = useState(recipe.title);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>(recipe.tags?.map(tag => tag.id) || []);
  const [submitting, setSubmitting] = useState(false);
  const [isImageFullScreenOpen, setIsImageFullScreenOpen] = useState(false);
  const { message, isError, resetFeedback, setFeedback, setMessage, setIsError } = useRecipeFormFeedback();

  const { author, token } = useAuth();
  const isAuthor = author?.id === recipe.author_id;
  const {
    ingredients,
    steps,
    ingredientsEditor,
    stepsEditor,
  } = useRecipeEditors({
    initialIngredients: recipe.ingredients,
    initialSteps: recipe.steps,
  });

  const handleTagLoadError = useCallback((nextMessage: string) => {
    setFeedback(nextMessage, true);
  }, [setFeedback]);

  const {
    availableTags,
    setAvailableTags,
  } = useRecipeTagOptions({
    onError: handleTagLoadError,
  });

  const {
    previewImage,
    handleImageChange,
    handleDeleteImage,
    resolveImageForSubmit,
    triggerAiGenerationIfNeeded,
  } = useEditRecipeImage({
    recipe,
    isAuthor,
    token,
    onRecipeUpdated,
    setMessage,
    setIsError,
    setSubmitting,
  });

  useEffect(() => {
    setTitle(recipe.title);
    setSelectedTagIds(recipe.tags?.map(tag => tag.id) || []);
    setIsImageFullScreenOpen(false);
  }, [recipe]);

  const closeFullScreenImage = useCallback(() => {
    setIsImageFullScreenOpen(false);
  }, []);

  useDismissibleOverlay({
    isOpen: isImageFullScreenOpen,
    onClose: closeFullScreenImage,
    disablePullToRefresh: true,
  });

  const { handleSubmit, handleDelete } = useEditRecipeFormActions({
    recipe,
    title,
    ingredients,
    steps,
    selectedTagIds,
    isAuthor,
    token,
    resolveImageForSubmit,
    triggerAiGenerationIfNeeded,
    onRecipeUpdated,
    onRecipeDeleted,
    setSubmitting,
    resetFeedback,
    setFeedback,
  });

  return (
    <Card shadow="sm" marginTop={4} padding={4}>
      <CardTitle as="h2" textAlign="center" marginBottom={4}>
        {isAuthor ? 'Modifier la recette' : 'Détails'}
      </CardTitle>
      {previewImage && (
        <>
          <PlainButton
            className="edit-recipe-image-button"
            marginBottom={4}
            onClick={() => setIsImageFullScreenOpen(true)}
            aria-label="Voir la photo en plein ecran"
          >
            <Image
              src={previewImage}
              alt={recipe.title}
              fluid
              rounded
            />
          </PlainButton>

          {isImageFullScreenOpen && (
            <Box
              className="edit-recipe-image-overlay"
              data-testid="edit-recipe-image-overlay"
              role="dialog"
              aria-modal="true"
              aria-label={`Photo de ${recipe.title}`}
            >
              <PlainButton
                className="edit-recipe-image-backdrop"
                aria-label="Fermer la photo en cliquant hors image"
                onClick={closeFullScreenImage}
              />
              <Box className="edit-recipe-image-frame">
                <PlainButton
                  className="edit-recipe-image-close-button"
                  aria-label="Fermer la photo"
                  onClick={closeFullScreenImage}
                >
                  x
                </PlainButton>
                <Image
                  src={previewImage}
                  alt={recipe.title}
                  className="edit-recipe-image-fullscreen"
                />
              </Box>
            </Box>
          )}
        </>
      )}
      {isAuthor && (
        <FormField label="Remplacer l'image" htmlFor="imageUpload">
          <FileInput id="imageUpload" accept="image/*" onChange={handleImageChange} />
          {previewImage && (
            <Button
              type="button"
              variant="danger"
              outline
              size="sm"
              marginTop={2}
              onClick={handleDeleteImage}
              disabled={submitting}
            >
              Supprimer l'image
            </Button>
          )}
        </FormField>
      )}
      {!isAuthor && <Alert variant="info">Mode lecture seule</Alert>}
      <Form onSubmit={handleSubmit}>
        <RecipeFormFields
          title={title}
          onTitleChange={setTitle}
          titleReadOnly={!isAuthor}
          ingredientsEditor={ingredientsEditor}
          stepsEditor={stepsEditor}
          availableTags={availableTags}
          selectedTagIds={selectedTagIds}
          setAvailableTags={setAvailableTags}
          setSelectedTagIds={setSelectedTagIds}
          isAuthor={isAuthor}
          submitting={submitting}
          onMessage={setFeedback}
          ingredientsEditorId="ingredients-editor-edit"
          stepsEditorId="steps-editor-edit"
          tagsLabelId="tags-label-edit"
        />

        {isAuthor && (
          <Flex justify="between">
            <Box>
              <Button type="submit" variant="success" marginEnd={2} disabled={submitting}>{submitting ? 'Mise à jour...' : 'Valider'}</Button>
              <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>Annuler</Button>
            </Box>
            <Button type="button" variant="danger" onClick={handleDelete} disabled={submitting}>{submitting ? 'Suppression...' : '🗑️'}</Button>
          </Flex>
        )}

        <RecipeFormAlert message={message} isError={isError} />
      </Form>
    </Card>
  );
};

export default EditRecipeForm;
